import { useMemo, useState } from 'react';

import { DeleteOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import { Checkbox, Col, PageHeader, Row, Switch } from 'antd';
import { Formik } from 'formik';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { T } from 'react-translator-component';
import { v4 as uuidv4 } from 'uuid';

import * as S from './styled';

import { updateDynamicPricingDetail } from '../../../../hooks/dynamicPricing';
import { offset } from '../../../../utils/dates';
import { capitalizeFirstLetter, getUrlPart } from '../../../../utils/strings';
import { COLORS } from '../../../../utils/theme/colors';
import { ReadOnlyAlert } from '../../../atoms/ReadOnlyAlert/ReadOnlyAlert';
import DynamicPricingConfigPeriodModal, {
  DynamicPricingModalConfig,
} from '../../../organisms/DynamicPricingConfigPeriodModal';
import SetPricingPageForm from '../../../organisms/SetPricingPageForm';
import {
  mascaraMoeda,
  SetPricingPageFormValues,
} from '../../../organisms/SetPricingPageForm/SetPricingPageForm';

const rawMaskMoney = (value: string, isComma = false) => {
  const onlyDigits = value
    .split('')
    .filter((s) => /\d/.test(s))
    .join('')
    .padStart(3, '0');

  const separator = isComma ? ',' : '.';

  const digitsFloat =
    onlyDigits.slice(0, -2) + separator + onlyDigits.slice(-2);

  return digitsFloat;
};

const getUser = () => JSON.parse(localStorage.getItem('auth-user') as any);

type Location = {
  pathname: string;
  state: {
    dynamicPricingDetail: SetPricingPageFormValues[];
    profileId: string;
    profileName: string;
    editable?: boolean;
    dynamicPricingDetailUuid?: string;
  };
};

const SetPricingPage = () => {
  const location: Location = useLocation();
  const history = useHistory();

  const canEdit = !!location?.state?.editable;

  const weekDays = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
  ];

  const [, , id, type] = location.pathname.split('/');
  const dayOfWeek = useMemo(
    () => (type === 'exception' ? uuidv4() : type.toUpperCase()),
    [type],
  );

  const dynamicPricingDetailPeriods =
    (location?.state?.dynamicPricingDetail &&
      location?.state?.dynamicPricingDetail.length > 0 &&
      location?.state?.dynamicPricingDetail.map(
        (dynamicPricingDetailPeriodMapped) => ({
          ...dynamicPricingDetailPeriodMapped,
          id: Math.random() * 3,
          servicePrice:
            dynamicPricingDetailPeriodMapped.servicePrice &&
            mascaraMoeda(
              Number(dynamicPricingDetailPeriodMapped.servicePrice).toFixed(2),
            ),
          kwhPrice:
            dynamicPricingDetailPeriodMapped.kwhPrice &&
            mascaraMoeda(
              Number(dynamicPricingDetailPeriodMapped.kwhPrice).toFixed(2),
            ),
          durationPrice:
            dynamicPricingDetailPeriodMapped.durationPrice &&
            mascaraMoeda(
              Number(dynamicPricingDetailPeriodMapped.durationPrice).toFixed(2),
            ),
          idleTimePrice:
            dynamicPricingDetailPeriodMapped.idleTimePrice &&
            mascaraMoeda(
              Number(dynamicPricingDetailPeriodMapped.idleTimePrice).toFixed(2),
            ),
          reservePrice:
            dynamicPricingDetailPeriodMapped.reservePrice &&
            mascaraMoeda(
              Number(dynamicPricingDetailPeriodMapped.reservePrice).toFixed(2),
            ),
          moneyPerKilowattCost:
            dynamicPricingDetailPeriodMapped.moneyPerKilowattCost &&
            mascaraMoeda(
              Number(
                dynamicPricingDetailPeriodMapped.moneyPerKilowattCost,
              ).toFixed(2),
            ),
          operationFee: String(
            Number(dynamicPricingDetailPeriodMapped.operationFee) * 100,
          ),
          taxes: String(Number(dynamicPricingDetailPeriodMapped.taxes) * 100),
        }),
      )) ||
    [];

  const isDynamicPricingActive = dynamicPricingDetailPeriods.some(
    (dynamicPricingDetailPeriod) => {
      return (
        (dynamicPricingDetailPeriod.active &&
          dynamicPricingDetailPeriod.dayOfWeek === dayOfWeek &&
          !dynamicPricingDetailPeriod.general) ||
        (dynamicPricingDetailPeriod.dynamicPricingDetailUuid ===
          location?.state?.dynamicPricingDetailUuid &&
          dynamicPricingDetailPeriod.active &&
          !dynamicPricingDetailPeriod.general)
      );
    },
  );

  const [loading, setLoading] = useState(false);

  const [dynamicPricingEnabled, setDynamicPricingEnabled] = useState(
    isDynamicPricingActive,
  );

  const [
    dynamicPricingConfigPeriodModalConfig,
    setDynamicPricingConfigPeriodModalConfig,
  ] = useState<DynamicPricingModalConfig>({
    visible: false,
    value: null,
  });
  const [dynamicPricingPeriods, setDynamicPricingPeriods] = useState<
    SetPricingPageFormValues[]
  >(dynamicPricingDetailPeriods);

  const [exceptionGeneral] = dynamicPricingDetailPeriods.filter(
    (item) =>
      item.dynamicPricingDetailUuid ===
      location?.state?.dynamicPricingDetailUuid,
  );

  const dynamicPricingDetailPeriodsFromDayOfWeek = dynamicPricingPeriods.filter(
    (dynamicPricingDetailPeriod) =>
      (dynamicPricingDetailPeriod.dayOfWeek === dayOfWeek &&
        !dynamicPricingDetailPeriod.general) ||
      (dynamicPricingDetailPeriod?.startDate === exceptionGeneral?.startDate &&
        dynamicPricingDetailPeriod?.endDate === exceptionGeneral?.endDate &&
        !dynamicPricingDetailPeriod.general),
  );

  const goBack = () =>
    history.push({
      pathname: `/dynamic-pricing/${getUrlPart(location.pathname, -2)}`,
      state: location.state,
    });

  const onSubmit = async (values: any) => {
    // Guarda: perfil de outro tenant é somente leitura
    if (!canEdit) return;

    setLoading(true);

    const dynamicPricing: any = {
      exception: values.exception,
      tenantPk: getUser().tenant_pk,
    };

    if (values.servicePrice) {
      dynamicPricing.servicePrice = Number(rawMaskMoney(values.servicePrice));
    }

    if (values.kwhPrice) {
      dynamicPricing.kwhPrice = Number(rawMaskMoney(values.kwhPrice));
    }

    if (values.durationPrice) {
      dynamicPricing.durationPrice = Number(rawMaskMoney(values.durationPrice));
    }

    if (values.operationFee) {
      dynamicPricing.operationFee =
        Number(values.operationFee.replace(/[%]/g, '').replace(/,/g, '.')) /
        100;
    }

    if (values.taxes) {
      dynamicPricing.taxes =
        Number(values.taxes.replace(/[%]/g, '').replace(/,/g, '.')) / 100;
    }

    if (values.idleTimePrice) {
      dynamicPricing.idleTimePrice = Number(rawMaskMoney(values.idleTimePrice));
    }

    if (values.reservePrice) {
      dynamicPricing.reservePrice = Number(rawMaskMoney(values.reservePrice));
    }

    if (values.moneyPerKilowattCost) {
      dynamicPricing.moneyPerKilowattCost = Number(
        rawMaskMoney(values.moneyPerKilowattCost),
      );
    }

    if (values.startDate) {
      dynamicPricing.startDate = values.startDate;
    }

    if (values.endDate) {
      dynamicPricing.endDate = values.endDate;
    }

    if (values.customIdleTime){
      dynamicPricing.customIdleTime = Number(values.customIdleTime);
    }

    const dynamicPricingPeriodsMapped = dynamicPricingPeriods.map(
      (dynamicPricingPeriod) => {
        delete dynamicPricingPeriod?.id;

        const payload = {
          ...dynamicPricingPeriod,
          startTimeGMT: dynamicPricingPeriod.startTimeGMT,
          endTimeGMT: dynamicPricingPeriod.endTimeGMT,
          kwhPrice:
            dynamicPricingPeriod.kwhPrice &&
            Number(rawMaskMoney(dynamicPricingPeriod.kwhPrice)),
          servicePrice:
            dynamicPricingPeriod.servicePrice &&
            Number(rawMaskMoney(dynamicPricingPeriod.servicePrice)),
          durationPrice:
            dynamicPricingPeriod.durationPrice &&
            Number(rawMaskMoney(dynamicPricingPeriod.durationPrice)),
          operationFee:
            Number(
              String(dynamicPricingPeriod.operationFee)
                .replace(/[%]/g, '')
                .replace(/,/g, '.'),
            ) / 100,
          taxes:
            Number(
              String(dynamicPricingPeriod.taxes)
                .replace(/[%]/g, '')
                .replace(/,/g, '.'),
            ) / 100,
          idleTimePrice:
            dynamicPricingPeriod.idleTimePrice &&
            Number(rawMaskMoney(dynamicPricingPeriod.idleTimePrice)),
          reservePrice:
            dynamicPricingPeriod.reservePrice &&
            Number(rawMaskMoney(dynamicPricingPeriod.reservePrice)),
          moneyPerKilowattCost:
            dynamicPricingPeriod.moneyPerKilowattCost &&
            Number(rawMaskMoney(dynamicPricingPeriod.moneyPerKilowattCost)),
          dayOfWeek: weekDays.includes(
            dynamicPricingPeriod.dayOfWeek?.toUpperCase(),
          )
            ? dynamicPricingPeriod.dayOfWeek.toUpperCase()
            : null,
          active:
            dayOfWeek === dynamicPricingPeriod.dayOfWeek?.toUpperCase() &&
            !dynamicPricingPeriod.general
              ? dynamicPricingEnabled
              : dynamicPricingPeriod.active,
          customIdleTime: dynamicPricingPeriod.customIdleTime && Number(dynamicPricingPeriod.customIdleTime),
        };

        return payload;
      },
    );

    let dynamicPricingDetailFormList = values.everyDayOfWeek
      ? [
          ...weekDays.flatMap((dayOfWeekMap: string) => [
            {
              ...dynamicPricing,
              dayOfWeek: dayOfWeekMap,
              general: true,
              active: true,
            },
            ...dynamicPricingPeriodsMapped.flatMap((dynamicPricingPeriod) => {
              if (
                dynamicPricingPeriod.dayOfWeek?.toUpperCase() ===
                  dayOfWeek.toUpperCase() &&
                !dynamicPricingPeriod.general
              ) {
                return [{ ...dynamicPricingPeriod, dayOfWeek: dayOfWeekMap }];
              }
              return [];
            }),
          ]),
          ...dynamicPricingPeriodsMapped.filter((item) => item.exception),
        ]
      : [
          ...(!dynamicPricingPeriodsMapped.some(
            (dynamicPricing) =>
              dynamicPricing.dayOfWeek === dayOfWeek.toUpperCase() &&
              dynamicPricing.general,
          )
            ? [
                {
                  ...dynamicPricing,
                  dayOfWeek: weekDays.includes(dayOfWeek.toUpperCase())
                    ? dayOfWeek.toUpperCase()
                    : null,
                  general: true,
                  active: true,
                },
              ]
            : []),

          ...dynamicPricingPeriodsMapped.map((dynamicPricingPeriod) => {
            if (
              dynamicPricingPeriod.dayOfWeek === dayOfWeek.toUpperCase() &&
              dynamicPricingPeriod.general
            ) {
              return {
                ...dynamicPricingPeriod,
                servicePrice: Number(rawMaskMoney(values.servicePrice)),
                kwhPrice: Number(rawMaskMoney(values.kwhPrice)),
                durationPrice: Number(rawMaskMoney(values.durationPrice)),
                idleTimePrice: Number(rawMaskMoney(values.idleTimePrice)),
                reservePrice: Number(rawMaskMoney(values.reservePrice)),
                moneyPerKilowattCost: Number(
                  rawMaskMoney(values.moneyPerKilowattCost),
                ),
                operationFee:
                  Number(
                    values.operationFee.replace(/[%]/g, '').replace(/,/g, '.'),
                  ) / 100,
                taxes:
                  Number(values.taxes.replace(/[%]/g, '').replace(/,/g, '.')) /
                  100,
                customIdleTime: Number(values.customIdleTime),
              };
            } else {
              if (dynamicPricingPeriod.dynamicPricingDetailUuid) {
                const [item] = dynamicPricingPeriodsMapped.filter(
                  (item) =>
                    item.dynamicPricingDetailUuid ===
                    dynamicPricingPeriod.dynamicPricingDetailUuid,
                );

                return item;
              } else {
                return {
                  ...dynamicPricingPeriod,
                };
              }
            }
          }),
        ];

    if (values.exception) {
      const newDate = moment(values.startDate);

      const [current]: any = location?.state?.dynamicPricingDetail?.filter(
        (item) => {
          if (!item || !item?.startDate) {
            return;
          }

          const date = moment(item.startDate);

          return newDate.isSame(date);
        },
      );

      if (current) {
        const currentProfiles = dynamicPricingDetailFormList.filter((item) => {
          const date = moment(item?.startDate);

          return !newDate.isSame(date);
        });

        const [editProfile] = dynamicPricingDetailFormList.filter((item) => {
          const date = moment(item?.startDate);

          return newDate.isSame(date);
        });

        dynamicPricingDetailFormList = [...currentProfiles, { ...editProfile }];
      }
    }

    try {
      // Origem do "Aplicar regra para todos os dias da semana": o back só recebe a lista
      // final e não teria como saber que os outros dias foram copiados deste.
      const replicatedFromDayOfWeek =
        values.everyDayOfWeek && weekDays.includes(dayOfWeek) ? dayOfWeek : null;

      await updateDynamicPricingDetail(id, {
        dynamicPricingDetailFormList,
        replicatedFromDayOfWeek,
      });

      goBack();
    } finally {
      setLoading(false);
    }
  };

  const verifyAlreadyHaveExceptionOnSamePeriod = (values, errors) => {
    const dynamicPricingPeriodsExceptions = dynamicPricingPeriods.filter(
      (item) => item.exception,
    );

    dynamicPricingPeriodsExceptions.some((periodException) => {
      if (
        values.dynamicPricingDetailUuid ===
        periodException.dynamicPricingDetailUuid
      ) {
        return false;
      }

      const startDate = moment(values.startDate);
      const endDate = moment(values.endDate);
      const exceptionStart = moment(periodException.startDate);
      const exceptionEnd = moment(periodException.endDate);

      const newIntervalExistsBetweenExceptionInterval =
        startDate.isBetween(exceptionStart, exceptionEnd, null, '[]') ||
        endDate.isBetween(exceptionStart, exceptionEnd, null, '[]');

      const existingIntervalBetweenNewInterval =
        exceptionStart.isBetween(startDate, endDate, null, '[]') ||
        exceptionEnd.isBetween(startDate, endDate, null, '[]');

      const startDateEqualsExceptionInterval =
        startDate.isSame(exceptionStart) || startDate.isSame(exceptionEnd);

      const endDateEqualsExceptionInterval =
        endDate.isSame(exceptionStart) || endDate.isSame(exceptionEnd);

      if (
        newIntervalExistsBetweenExceptionInterval ||
        existingIntervalBetweenNewInterval ||
        startDateEqualsExceptionInterval ||
        endDateEqualsExceptionInterval
      ) {
        errors.startDate =
          'Esta data já está contida em outro período de exceção';
        errors.endDate =
          'Esta data já está contida em outro período de exceção';
        return true;
      }

      if (startDate.isBefore(exceptionStart) && endDate.isAfter(exceptionEnd)) {
        errors.startDate =
          'O novo período não pode englobar outro período de exceção';
        errors.endDate =
          'O novo período não pode englobar outro período de exceção';
        return true;
      }

      return false;
    });

    if (moment(values.startDate).isAfter(moment(values.endDate))) {
      errors.startDate = 'A data inicial deve ser menor que a data final';
    }
  };

  const isWeekday = getUrlPart(location.pathname, -1) !== 'exception';
  const isException = getUrlPart(location.pathname, -1) === 'exception';

  return (
    <>
      <S.Wrapper>
        <PageHeader
          onBack={goBack}
          title={T(
            capitalizeFirstLetter(getUrlPart(location.pathname, -1) as string),
          )}
        />

        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          validate={(values) => {
            const errors: any = {};

            verifyAlreadyHaveExceptionOnSamePeriod(values, errors);

            if (values.exception && !values.startDate) {
              errors.startDate = 'Selecione o dia';
            }

            if (!values.durationPrice && !values.kwhPrice) {
              errors.durationPrice =
                'É necessário definir ou o preço por duração ou preço por kWh';
              errors.kwhPrice =
                'É necessário definir ou o preço por duração ou preço por kWh';
            }

            if ((values.customIdleTime < 1 || values.customIdleTime > 60) && values.isCustomIdleTimeEnabled) {
              errors.customIdleTime = 'O tempo de espera personalizado deve estar entre 1 e 60 minutos';
            }

            return errors;
          }}
          initialValues={{
            servicePrice: '',
            kwhPrice: '',
            durationPrice: '',
            operationFee: '',
            taxes: '',
            idleTimePrice: '',
            reservePrice: '',
            moneyPerKilowattCost: '',
            everyDayOfWeek: false,
            general: true,
            startDate: null,
            endDate: null,
          }}
          onSubmit={onSubmit}
        >
          {({ handleChange, handleSubmit, values }) => {
            return (
              <S.Blank>
                {dynamicPricingConfigPeriodModalConfig.visible && (
                  <DynamicPricingConfigPeriodModal
                    dayOfWeek={dayOfWeek}
                    setDynamicPricingConfigModal={
                      setDynamicPricingConfigPeriodModalConfig
                    }
                    dynamicPricingConfigModal={
                      dynamicPricingConfigPeriodModalConfig
                    }
                    dynamicPricingPeriods={dynamicPricingPeriods}
                    setDynamicPricingPeriods={setDynamicPricingPeriods}
                    startDate={values.startDate as any}
                    endDate={values.endDate as any}
                  />
                )}

                {!canEdit && <ReadOnlyAlert style={{ marginBottom: 24 }} />}

                <form onSubmit={handleSubmit}>
                  <SetPricingPageForm
                    loading={loading || !canEdit}
                    selectedValue={location?.state?.dynamicPricingDetail}
                    dayOfWeek={dayOfWeek}
                    isException={isException}
                    dynamicPricingDetailUuid={
                      location?.state?.dynamicPricingDetailUuid
                    }
                  />

                  {!values.exception && (
                    <S.DynamicPricingWrapper
                      dynamicPricingEnabled={dynamicPricingEnabled}
                    >
                      <S.DynamicPricingSwitchWrapper>
                        <Switch
                          disabled={loading || !canEdit}
                          checked={dynamicPricingEnabled}
                          onChange={() =>
                            setDynamicPricingEnabled((prevState) => !prevState)
                          }
                        />
                        Habilitar tarifação dinâmica
                      </S.DynamicPricingSwitchWrapper>

                      {dynamicPricingEnabled && (
                        <>
                          {!dynamicPricingDetailPeriodsFromDayOfWeek ||
                            (!dynamicPricingDetailPeriodsFromDayOfWeek.length &&
                              canEdit && (
                              <S.DynamicPricingConfigurationContainer
                                onClick={() => {
                                  setDynamicPricingConfigPeriodModalConfig({
                                    visible: true,
                                  });
                                }}
                              >
                                <S.DynamicPricingConfigurationTitle>
                                  <S.DynamicPricingConfigLabel>
                                    Configurar tarifação
                                  </S.DynamicPricingConfigLabel>

                                  <RightOutlined
                                    style={{
                                      marginBottom: 2,
                                      marginLeft: 2,
                                      fontSize: 14,
                                      color: COLORS.black,
                                    }}
                                  />
                                </S.DynamicPricingConfigurationTitle>
                              </S.DynamicPricingConfigurationContainer>
                            ))}

                          {dynamicPricingDetailPeriodsFromDayOfWeek &&
                            dynamicPricingDetailPeriodsFromDayOfWeek.length >
                              0 && (
                              <>
                                {dynamicPricingDetailPeriodsFromDayOfWeek.map(
                                  (dynamicPricingPeriod, index) => {
                                    return (
                                      <S.DynamicPricingConfigurationContainer
                                        key={index}
                                        onClick={() => {
                                          if (!canEdit) return;

                                          setDynamicPricingConfigPeriodModalConfig(
                                            {
                                              visible: true,
                                              value: dynamicPricingPeriod,
                                            },
                                          );
                                        }}
                                      >
                                        <S.DynamicPricingConfigurationTitle>
                                          <S.DynamicPricingConfigLabel>
                                            Configurar tarifação
                                          </S.DynamicPricingConfigLabel>

                                          <RightOutlined
                                            style={{
                                              marginBottom: 2,
                                              marginLeft: 2,
                                              fontSize: 14,
                                              color: COLORS.black,
                                            }}
                                          />

                                          {canEdit && (
                                            <S.ThrashButton
                                              style={{ marginLeft: 'auto' }}
                                              type='primary'
                                              onClick={(e) => {
                                                e.stopPropagation();

                                                setDynamicPricingPeriods(
                                                  (prevState) =>
                                                    prevState.filter(
                                                      (item) =>
                                                        item.id !==
                                                        dynamicPricingPeriod.id,
                                                    ),
                                                );
                                              }}
                                            >
                                              <DeleteOutlined
                                                size={16}
                                                style={{ color: COLORS.black }}
                                              />
                                            </S.ThrashButton>
                                          )}
                                        </S.DynamicPricingConfigurationTitle>

                                        <Row gutter={[32, 16]}>
                                          <Col>
                                            <S.Label>Hora de início</S.Label>

                                            <S.Value>
                                              {dynamicPricingPeriod.startTimeGMT
                                                ? moment
                                                    .parseZone(
                                                      dynamicPricingPeriod.startTimeGMT,
                                                      'HH:mm:ss',
                                                    )
                                                    .utcOffset(offset)
                                                    .format('HH:mm:ss')
                                                : '00:00:00'}
                                            </S.Value>
                                          </Col>

                                          <Col>
                                            <S.Label>Hora de término</S.Label>

                                            <S.Value>
                                              {dynamicPricingPeriod.endTimeGMT
                                                ? moment
                                                    .parseZone(
                                                      dynamicPricingPeriod.endTimeGMT,
                                                      'HH:mm:ss',
                                                    )
                                                    .utcOffset(offset)
                                                    .format('HH:mm:ss')
                                                : '00:00:00'}
                                            </S.Value>
                                          </Col>

                                          <Col>
                                            <S.Label>Valor de início</S.Label>

                                            <S.Value>
                                              {dynamicPricingPeriod.servicePrice
                                                ? dynamicPricingPeriod.servicePrice
                                                : 'R$ 0,00'}
                                            </S.Value>
                                          </Col>

                                          <Col>
                                            <S.Label>Preço por kWh</S.Label>

                                            <S.Value>
                                              {dynamicPricingPeriod.kwhPrice
                                                ? dynamicPricingPeriod.kwhPrice
                                                : 'R$ 0,00'}
                                            </S.Value>
                                          </Col>

                                          <Col>
                                            <S.Label>Preço por tempo</S.Label>

                                            <S.Value>
                                              {dynamicPricingPeriod.durationPrice
                                                ? dynamicPricingPeriod.durationPrice
                                                : 'R$ 0,00'}
                                            </S.Value>
                                          </Col>

                                          <Col>
                                            <S.Label>Taxa de operação</S.Label>

                                            <S.Value>
                                              {dynamicPricingPeriod.operationFee
                                                ? `${String(
                                                    dynamicPricingPeriod.operationFee,
                                                  )
                                                    .replace(/[%]/g, '')
                                                    .replace(/,/g, '.')}%`
                                                : '0,0%'}
                                            </S.Value>
                                          </Col>

                                          <Col>
                                            <S.Label>Impostos</S.Label>

                                            <S.Value>
                                              {dynamicPricingPeriod.taxes
                                                ? `${String(
                                                    dynamicPricingPeriod.taxes,
                                                  )
                                                    .replace(/[%]/g, '')
                                                    .replace(/,/g, '.')}%`
                                                : '0,0%'}
                                            </S.Value>
                                          </Col>

                                          <Col>
                                            <S.Label>
                                              Ociosidade conector
                                            </S.Label>

                                            <S.Value>
                                              <S.Value>
                                                {dynamicPricingPeriod.idleTimePrice
                                                  ? `R$ ${rawMaskMoney(
                                                      dynamicPricingPeriod.idleTimePrice,
                                                      true,
                                                    )}`
                                                  : 'R$ 0,00'}
                                              </S.Value>
                                            </S.Value>
                                          </Col>

                                          <Col>
                                            <S.Label>Custo por kWh</S.Label>

                                            <S.Value>
                                              {dynamicPricingPeriod.moneyPerKilowattCost
                                                ? dynamicPricingPeriod.moneyPerKilowattCost
                                                : 'R$ 0,00'}
                                            </S.Value>
                                          </Col>
                                        </Row>
                                      </S.DynamicPricingConfigurationContainer>
                                    );
                                  },
                                )}

                                {canEdit && (
                                  <S.RoundedButton
                                    type='primary'
                                    onClick={() => {
                                      setDynamicPricingConfigPeriodModalConfig({
                                        visible: true,
                                      });
                                    }}
                                  >
                                    <PlusOutlined />
                                  </S.RoundedButton>
                                )}
                              </>
                            )}
                        </>
                      )}
                    </S.DynamicPricingWrapper>
                  )}

                  {isWeekday && (
                    <Row style={{ marginTop: 32 }}>
                      <S.CheckWrapper>
                        <Checkbox
                          name='everyDayOfWeek'
                          disabled={loading || !canEdit}
                          onChange={handleChange}
                          checked={values.everyDayOfWeek}
                        >
                          Aplicar regra para todos os dias da semana
                        </Checkbox>
                      </S.CheckWrapper>
                    </Row>
                  )}

                  <Row
                    style={{ marginTop: 32, justifyContent: 'space-between' }}
                  >
                    <S.Button
                      danger={canEdit}
                      disabled={loading}
                      size='large'
                      onClick={goBack}
                    >
                      {canEdit ? 'Cancelar' : 'Voltar'}
                    </S.Button>

                    {canEdit && (
                      <S.Button
                        htmlType='submit'
                        loading={loading}
                        size='large'
                        type='primary'
                      >
                        Definir regra
                      </S.Button>
                    )}
                  </Row>
                </form>
              </S.Blank>
            );
          }}
        </Formik>
      </S.Wrapper>
    </>
  );
};

export default SetPricingPage;
